import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  CalendarDays, CalendarRange, CreditCard, Handshake, Link2, Receipt, TrendingUp, UserPlus, Users, UsersRound, Wallet,
} from "lucide-react";
import { getTransactionStats } from "../../business/PaymentBusiness";
import { getAllUsers } from "../../business/UserBusiness";
import { Card, CardTitle, EmptyState, Page, PageHeader, Skeleton, Stat } from "../../components/ui";

// Ranking com barra proporcional ao primeiro colocado
function RankingCard({ title, icon: Icon, items, unit, color, loading }) {
  const max = items[0]?.count || 1;
  return (
    <Card className="p-5">
      <CardTitle right={<Icon className="size-4 text-neutral-400" />}>{title}</CardTitle>
      {loading ? (
        <div className="mt-4 flex flex-col gap-4">{Array.from({ length: 5 }, (_, i) => <Skeleton key={i} className="h-8 w-full" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={Icon} title="Nada registrado ainda" />
      ) : (
        <ol className="mt-4 flex flex-col gap-3.5">
          {items.map(({ category, count }, index) => (
            <li key={category}>
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="flex min-w-0 items-baseline gap-2">
                  <span className="w-4 shrink-0 text-xs text-neutral-400">{index + 1}</span>
                  <span className="truncate text-neutral-900 dark:text-neutral-100">{category}</span>
                </span>
                <span className="shrink-0 text-neutral-500">{count} {count === 1 ? unit[0] : unit[1]}</span>
              </div>
              <div className="ml-6 mt-1.5 h-1.5 rounded-full bg-neutral-100 dark:bg-white/[0.05]">
                <div className="h-full rounded-full" style={{ width: `${(count / max) * 100}%`, background: color }} />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

export const Reports = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState({
    totalTransacted: 0,
    totalTransactions: 0,
    totalAppointments: 0,
    averageTransactionValue: 0,
    mostActiveMonth: "",
    mostActiveDay: "",
    topCategories: [],
    topAppointmentCategories: [],
    userGrowth: 0,
    activeUsers: 0,
    premiumUsers: 0,
    googleIntegratedUsers: 0,
    sharedAccounts: 0,
  });

  const adminName = localStorage.getItem("@Auth:name");

  // Usuários contados aqui; os números das transações vêm prontos do backend
  const calculateMetrics = useCallback((stats, usersData) => {
    const safeUsers = Array.isArray(usersData) ? usersData : [];

    const activeUsers = safeUsers.filter((user) => user?.subscription === "active").length;
    const premiumUsers = safeUsers.filter((user) => user?.role === "patner").length;
    const googleIntegratedUsers = safeUsers.filter(
      (user) => user?.google?.connected === true
    ).length;
    const sharedAccounts = safeUsers.filter(
      (user) => user?.sharedAccounts && Object.keys(user.sharedAccounts).length > 0
    ).length;

    const lastMonth = new Date();
    lastMonth.setMonth(lastMonth.getMonth() - 1);
    const recentUsers = safeUsers.filter(
      (user) => user?.createdAt && new Date(user.createdAt) > lastMonth
    ).length;

    setMetrics({
      totalTransacted: stats?.totalTransacted || 0,
      totalTransactions: stats?.totalTransactions || 0,
      totalAppointments: stats?.totalAppointments || 0,
      averageTransactionValue: stats?.averageTransactionValue || 0,
      mostActiveMonth: stats?.mostActiveMonth || "",
      mostActiveDay: stats?.mostActiveDay || "",
      topCategories: stats?.topCategories || [],
      topAppointmentCategories: stats?.topAppointmentCategories || [],
      userGrowth: recentUsers,
      activeUsers,
      premiumUsers,
      googleIntegratedUsers,
      sharedAccounts,
    });
  }, []);

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      const [stats, usersResponse] = await Promise.all([
        getTransactionStats(),
        getAllUsers(),
      ]);

      const usersData = Array.isArray(usersResponse?.data) ? usersResponse.data : [];

      setUsers(usersData);
      calculateMetrics(stats, usersData);
    } catch (error) {
      console.error("Erro ao buscar dados:", error);
      toast.error("Erro ao carregar relatórios");
    } finally {
      setIsLoading(false);
    }
  }, [calculateMetrics]);

  const formatCurrency = (amount) => {
    return amount.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatMonth = (monthKey) => {
    if (!monthKey) return "—";
    const [year, month] = monthKey.split("-");
    const date = new Date(parseInt(year, 10), parseInt(month, 10) - 1);
    const label = date.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
    return label.charAt(0).toUpperCase() + label.slice(1);
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const total = users.length;
  const share = (n) => (total ? `${Math.round((n / total) * 100)}% dos usuários` : null);

  return (
    <Page>
      <PageHeader title="Relatórios" description={`Olá, ${adminName || 'admin'}. O que a Nath registrou e como as contas usam o Controlly.`} />

      <p className="pt-1 text-sm font-medium text-neutral-500 dark:text-neutral-400">Registros</p>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Total transacionado" value={formatCurrency(metrics.totalTransacted)} icon={Wallet} tone="green" loading={isLoading} />
        <Stat label="Lançamentos financeiros" value={metrics.totalTransactions} icon={Receipt} tone="blue" loading={isLoading} hint="Gastos, entradas, contas fixas e parcelas" />
        <Stat label="Compromissos" value={metrics.totalAppointments} icon={CalendarDays} tone="amber" loading={isLoading} />
        <Stat label="Valor médio" value={formatCurrency(metrics.averageTransactionValue)} icon={CreditCard} loading={isLoading} hint="Por lançamento financeiro" />
      </div>

      <p className="pt-2 text-sm font-medium text-neutral-500 dark:text-neutral-400">Contas</p>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-6">
        <Stat label="Total de usuários" value={total} icon={Users} loading={isLoading} />
        <Stat label="Assinaturas ativas" value={metrics.activeUsers} icon={TrendingUp} tone="green" loading={isLoading} hint={share(metrics.activeUsers)} />
        <Stat label="Novos no último mês" value={metrics.userGrowth} icon={UserPlus} tone="blue" loading={isLoading} />
        <Stat label="Google Agenda conectado" value={metrics.googleIntegratedUsers} icon={Link2} tone="amber" loading={isLoading} hint={share(metrics.googleIntegratedUsers)} />
        <Stat label="Contas compartilhadas" value={metrics.sharedAccounts} icon={UsersRound} loading={isLoading} />
        <Stat label="Parceiros" value={metrics.premiumUsers} icon={Handshake} loading={isLoading} />
      </div>

      <div className="grid gap-4 pt-2 lg:grid-cols-2">
        <Card className="p-5">
          <CardTitle right={<CalendarRange className="size-4 text-neutral-400" />}>Mês mais movimentado</CardTitle>
          {isLoading ? <Skeleton className="mt-3 h-7 w-40" /> : <p className="mt-2 text-2xl font-medium tracking-tight">{formatMonth(metrics.mostActiveMonth)}</p>}
        </Card>
        <Card className="p-5">
          <CardTitle right={<CalendarDays className="size-4 text-neutral-400" />}>Dia da semana mais movimentado</CardTitle>
          {isLoading ? <Skeleton className="mt-3 h-7 w-32" /> : <p className="mt-2 text-2xl font-medium tracking-tight">{metrics.mostActiveDay || '—'}</p>}
        </Card>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <RankingCard title="Categorias mais usadas nas finanças" icon={Receipt} items={metrics.topCategories} unit={['lançamento', 'lançamentos']} color="#10b981" loading={isLoading} />
        <RankingCard title="Categorias mais usadas na agenda" icon={CalendarDays} items={metrics.topAppointmentCategories} unit={['compromisso', 'compromissos']} color="#38bdf8" loading={isLoading} />
      </div>
    </Page>
  );
};
